import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toSdksDetails } from '@gonfalon/navigator/navigators';
import type { SdkVersionDetailsRep } from '@gonfalon/openapi/types';
import {
  Cell,
  Column,
  Focusable,
  Heading,
  Input,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TagGroup,
  TagList,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { SDKData } from './types';
import { getStatusTagVariant, mapEolStatusToStatus, SDKStatus, SortDirection, SortField } from './types';
import { useSdkVersionsDetails } from './useSdkVersionsDetails';

import styles from './SDKUsage.module.css';

// Status severity ranking (lower number = worse status)
const statusSeverityRank: Record<SDKStatus, number> = {
  [SDKStatus.OUT_OF_DATE]: 1, // Worst
  [SDKStatus.EOL_APPROACHING]: 2,
  [SDKStatus.UNKNOWN]: 3,
  [SDKStatus.MAJOR_VERSION_AVAILABLE]: 4,
  [SDKStatus.UP_TO_DATE]: 5, // Best
};

// Determine worst status from multiple SDK statuses.
// Ignore UNKNOWN when any known status exists so unmapped packages (including
// AI SDKs without EOL metadata) do not downgrade an otherwise healthy app.
// If every status is unknown, keep Status undetermined.
const getWorstStatus = (statuses: SDKStatus[]): SDKStatus => {
  const known = statuses.filter((status) => status !== SDKStatus.UNKNOWN);
  const ranked = known.length > 0 ? known : [SDKStatus.UNKNOWN];

  if (ranked.includes(SDKStatus.OUT_OF_DATE)) {
    return SDKStatus.OUT_OF_DATE;
  }
  if (ranked.includes(SDKStatus.EOL_APPROACHING)) {
    return SDKStatus.EOL_APPROACHING;
  }
  if (ranked.includes(SDKStatus.MAJOR_VERSION_AVAILABLE)) {
    return SDKStatus.MAJOR_VERSION_AVAILABLE;
  }
  if (ranked.includes(SDKStatus.UNKNOWN)) {
    return SDKStatus.UNKNOWN;
  }
  return SDKStatus.UP_TO_DATE;
};

type SDKUsageProps = {
  onApplicationClick?: (applicationId: string) => string;
};

export function SDKUsage({ onApplicationClick }: SDKUsageProps = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>(SortField.APPLICATION);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);

  // Fetch SDK version details from the API
  const { data: apiResponse } = useSdkVersionsDetails();

  // Transform API data to match component's data structure
  const sdkData = useMemo((): SDKData[] => {
    if (!apiResponse) {
      return [];
    }

    // Group SDK records by applicationId
    const appMap = new Map<string, { sdks: Set<string>; statuses: SDKStatus[] }>();

    apiResponse.forEach((sdk: SdkVersionDetailsRep) => {
      const appId = sdk.applicationId || 'unknown';
      let app = appMap.get(appId);
      if (!app) {
        app = { sdks: new Set(), statuses: [] };
        appMap.set(appId, app);
      }

      // Add SDK name if present
      if (sdk.name) {
        app.sdks.add(sdk.name);
      }

      // Add SDK status
      app.statuses.push(mapEolStatusToStatus(sdk.eolStatus));
    });

    // Convert map to array format
    return Array.from(appMap.entries()).map(
      ([appId, { sdks, statuses }]): SDKData => ({
        id: appId,
        application: appId, // Using applicationId as display name for now
        connectedSdks: Array.from(sdks).sort(),
        status: getWorstStatus(statuses),
      }),
    );
  }, [apiResponse]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
    } else {
      setSortField(field);
      setSortDirection(SortDirection.ASC);
    }
  };

  const filteredAndSortedData = useMemo(() => {
    // Filter based on search query
    let filtered = sdkData.filter((item) => {
      const query = searchQuery.toLowerCase();
      return (
        item.application.toLowerCase().includes(query) ||
        item.connectedSdks.some((sdk) => sdk.toLowerCase().includes(query))
      );
    });

    // Sort the filtered data
    filtered = filtered.sort((a, b) => {
      let aValue: string | number | string[] = a[sortField];
      let bValue: string | number | string[] = b[sortField];

      // Handle status sorting by severity rank
      if (sortField === SortField.STATUS) {
        const aRank = statusSeverityRank[a.status];
        const bRank = statusSeverityRank[b.status];
        return sortDirection === SortDirection.ASC ? aRank - bRank : bRank - aRank;
      }

      // Handle array values (connectedSdks)
      if (Array.isArray(aValue) && Array.isArray(bValue)) {
        // Sort by number of SDKs, then by first SDK name alphabetically
        if (aValue.length !== bValue.length) {
          return sortDirection === SortDirection.ASC ? aValue.length - bValue.length : bValue.length - aValue.length;
        }
        aValue = aValue[0]?.toLowerCase() || '';
        bValue = bValue[0]?.toLowerCase() || '';
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue as string).toLowerCase();
      }

      if (aValue < bValue) {
        return sortDirection === SortDirection.ASC ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === SortDirection.ASC ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [sdkData, searchQuery, sortField, sortDirection]);

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return <Icon name="sort" size="small" />;
    }
    return sortDirection === SortDirection.ASC ? (
      <Icon name="sort-asc" size="small" />
    ) : (
      <Icon name="sort-desc" size="small" />
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Heading size="large" className={styles.heading}>
          <Message id="billing.sdkConnections.title" />
        </Heading>
        <Text size="medium">
          <Message id="billing.sdkConnections.monitorSdkConnections" />
        </Text>
      </div>

      <div className={styles.searchContainer}>
        <Input
          type="search"
          placeholder={msg('billing.sdkConnections.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
          data-test-id="sdks-search-input"
        />
      </div>

      {filteredAndSortedData.length === 0 ? (
        <Text>
          <Message id="billing.sdkConnections.noApplicationsFound" />
        </Text>
      ) : (
        <div className={styles.tableWrapper}>
          <Table
            aria-label={msg('billing.sdkConnections.tableLabel')}
            className={styles.table}
            data-test-id="sdks-table"
          >
            <TableHeader
              columns={[
                {
                  id: SortField.APPLICATION,
                  name: msg('billing.sdkConnections.applicationColumn'),
                  isRowHeader: true,
                },
                { id: SortField.CONNECTED_SDKS, name: msg('billing.sdkConnections.connectedSdksColumn') },
                { id: SortField.STATUS, name: msg('billing.sdkConnections.statusColumn') },
              ]}
            >
              {(column) => (
                <Column
                  isRowHeader={column.isRowHeader}
                  className={styles.sortableHeader}
                  onClick={() => handleSort(column.id)}
                >
                  {column.name} {getSortIndicator(column.id)}
                </Column>
              )}
            </TableHeader>
            <TableBody>
              {filteredAndSortedData.map((item) => (
                <Row key={item.id} data-test-id={`sdk-row-${item.id}`}>
                  <Cell>
                    <TooltipTrigger>
                      <Focusable>
                        <Link
                          to={onApplicationClick?.(item.id) ?? toSdksDetails({ applicationId: item.id })}
                          className={`${styles.applicationLink} ${styles.applicationCell}`}
                        >
                          {item.application}
                        </Link>
                      </Focusable>
                      <Tooltip placement="top left">{item.application}</Tooltip>
                    </TooltipTrigger>
                  </Cell>
                  <Cell>
                    <TooltipTrigger>
                      <Focusable>
                        <span
                          role="button" // Needed for tooltip
                          aria-label={item.connectedSdks.join(', ')}
                          className={styles.connectedSdksCell}
                        >
                          {item.connectedSdks.join(', ')}
                        </span>
                      </Focusable>
                      <Tooltip placement="top left">{item.connectedSdks.join(', ')}</Tooltip>
                    </TooltipTrigger>
                  </Cell>
                  <Cell>
                    <TagGroup aria-label={msg('billing.sdkConnections.statusLabel')}>
                      <TagList>
                        <Tag variant={getStatusTagVariant(item.status)} data-test-id={`sdk-status-${item.id}`}>
                          {item.status}
                        </Tag>
                      </TagList>
                    </TagGroup>
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {filteredAndSortedData.length > 0 && (
        <Text size="small">
          <Message
            id="billing.sdkConnections.showingApplications"
            values={{ shown: filteredAndSortedData.length, total: sdkData.length }}
          />
        </Text>
      )}
    </div>
  );
}
